/**
 * FloraHut Cart Reminder Section Web Component
 * High-performance, lightweight, multi-currency compatible
 * Compliant with GEMINI.md senior theme architecture
 */
(function () {
  'use strict';

  if (customElements.get('cart-reminder')) return;

  class CartReminder extends HTMLElement {
    constructor() {
      super();

      this.closeBtn = this.querySelector('.cart-reminder__close');
      this.imageEl = this.querySelector('.cart-reminder__img');
      this.titleEl = this.querySelector('.cart-reminder__title');
      this.countBadgeEl = this.querySelector('.cart-reminder__count-badge');
      this.priceEl = this.querySelector('.cart-reminder__price');
      this.compareEl = this.querySelector('.cart-reminder__compare');
      this.discountEl = this.querySelector('.cart-reminder__discount');
      this.orderBtn = this.querySelector('.cart-reminder__btn');

      const rawExclusions = this.dataset.excludedTitles || '';
      this.excludedTitles = rawExclusions
        .split(',')
        .map(t => t.trim().toLowerCase())
        .filter(Boolean);

      this.debounceTimer = null;
      this.isDismissed = false;
    }

    connectedCallback() {
      // Check session dismissal state
      try {
        if (sessionStorage.getItem('fh_cart_reminder_dismissed') === 'true') {
          this.hidden = true;
          this.style.display = 'none';
          this.isDismissed = true;
          return;
        }
      } catch (e) {}

      // Close / Dismiss button handler
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.dismiss();
        });
      }

      // 1. Subscribe to theme PubSub cartUpdate event
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.cartData) {
            this.handleCartData(event.cartData);
          } else {
            this.scheduleFetch();
          }
        });
      }

      // 2. Global Fetch Interceptor for instantaneous updates on cart changes
      this.initFetchInterceptor();

      // 3. Multi-currency changed event
      window.addEventListener('currency:changed', () => {
        if (!this.hidden && !this.isDismissed) {
          this.fetchAndRefresh();
        }
      });

      // 4. Initial fetch if not pre-rendered on server or marked for check
      if (this.hasAttribute('data-initial-fetch')) {
        this.fetchAndRefresh();
      }
    }

    dismiss() {
      this.isDismissed = true;
      try {
        sessionStorage.setItem('fh_cart_reminder_dismissed', 'true');
      } catch (e) {}

      this.style.transition = 'opacity 0.24s ease, transform 0.24s ease';
      this.style.opacity = '0';
      this.style.transform = 'translateY(8px)';

      setTimeout(() => {
        this.hidden = true;
        this.style.display = 'none';
      }, 250);
    }

    initFetchInterceptor() {
      if (window._fhCartReminderInterceptorSet) return;
      window._fhCartReminderInterceptorSet = true;

      const origFetch = window.fetch;
      window.fetch = function (input, init) {
        const url = typeof input === 'string' ? input : (input && input.url ? input.url : '');
        return origFetch.apply(this, arguments).then((response) => {
          if (
            url &&
            (url.includes('/cart/add') ||
              url.includes('/cart/change') ||
              url.includes('/cart/update') ||
              url.includes('/cart/clear'))
          ) {
            try {
              response.clone().json().then((cartData) => {
                document.querySelectorAll('cart-reminder').forEach((el) => {
                  if (typeof el.handleCartData === 'function') {
                    el.handleCartData(cartData);
                  }
                });
              }).catch(() => {
                document.querySelectorAll('cart-reminder').forEach((el) => {
                  if (typeof el.scheduleFetch === 'function') {
                    el.scheduleFetch();
                  }
                });
              });
            } catch (err) {
              document.querySelectorAll('cart-reminder').forEach((el) => {
                if (typeof el.scheduleFetch === 'function') {
                  el.scheduleFetch();
                }
              });
            }
          }
          return response;
        });
      };
    }

    scheduleFetch() {
      if (this.isDismissed) return;
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.fetchAndRefresh();
      }, 300);
    }

    fetchAndRefresh() {
      if (this.isDismissed) return;
      fetch('/cart.js')
        .then((res) => res.json())
        .then((cart) => this.handleCartData(cart))
        .catch(() => {});
    }

    isItemExcluded(item) {
      if (!item) return true;
      const title = (item.product_title || item.title || '').toLowerCase();
      const handle = (item.handle || '').toLowerCase();

      // Check configured exclusions
      for (let i = 0; i < this.excludedTitles.length; i++) {
        if (title.includes(this.excludedTitles[i])) {
          return true;
        }
      }

      // Check delivery items / handles
      if (title.includes('delivery') || handle.includes('delivery')) {
        return true;
      }

      return false;
    }

    formatMoney(cents) {
      if (typeof window.formatMoneyActive === 'function') {
        return String(window.formatMoneyActive(cents)).replace(/<[^>]*>/g, '').trim();
      }
      return '₹' + (cents / 100).toFixed(0);
    }

    handleCartData(cart) {
      if (this.isDismissed) return;

      if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
        this.hidden = true;
        this.style.display = 'none';
        return;
      }

      const validItems = cart.items.filter((item) => !this.isItemExcluded(item));
      if (validItems.length === 0) {
        this.hidden = true;
        this.style.display = 'none';
        return;
      }

      const firstItem = validItems[0];
      const extraCount = validItems.length - 1;

      // Update Image
      if (this.imageEl && firstItem.image) {
        this.imageEl.src = firstItem.image;
        this.imageEl.alt = firstItem.product_title || 'Product Image';
      }

      // Update Title
      if (this.titleEl) {
        this.titleEl.textContent = firstItem.product_title || firstItem.title;
      }

      // Update Extra Items Badge
      if (this.countBadgeEl) {
        if (extraCount > 0) {
          this.countBadgeEl.textContent = `+${extraCount} more item${extraCount > 1 ? 's' : ''}`;
          this.countBadgeEl.style.display = 'inline-block';
        } else {
          this.countBadgeEl.style.display = 'none';
        }
      }

      // Calculate Prices & Savings
      const finalPrice = firstItem.final_line_price != null ? firstItem.final_line_price : (firstItem.final_price || firstItem.price || 0);
      const comparePrice = firstItem.original_line_price != null ? firstItem.original_line_price : (firstItem.line_price || firstItem.original_price || finalPrice);

      if (this.priceEl) {
        this.priceEl.innerHTML = this.formatMoney(finalPrice);
      }

      if (this.compareEl && this.discountEl) {
        if (comparePrice > finalPrice) {
          this.compareEl.innerHTML = this.formatMoney(comparePrice);
          this.compareEl.style.display = 'inline';

          const pct = Math.round((1 - (finalPrice / comparePrice)) * 100);
          if (pct > 0) {
            this.discountEl.textContent = `${pct}% OFF`;
            this.discountEl.style.display = 'inline-block';
          } else {
            this.discountEl.style.display = 'none';
          }
        } else {
          this.compareEl.style.display = 'none';
          this.discountEl.style.display = 'none';
        }
      }

      // Reveal reminder smoothly
      this.hidden = false;
      this.style.display = 'block';
    }
  }

  customElements.define('cart-reminder', CartReminder);
})();
